<template>
    <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black opacity-50 modal-wrapper" @click="attemptClose"></div>

        <!-- Modal -->
        <div
            class="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl sm:max-w-7xl flex flex-col overflow-hidden z-10">
            <!-- Success Top Bar -->
            <div v-if="showSuccessMessage"
                class="absolute top-0 left-0 right-0 bg-primary-500 text-white text-center py-3 sm:rounded-t-xl z-20 transition-all duration-300">
                <div class="flex items-center justify-center gap-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {{ successMessage }}
                </div>
            </div>

            <!-- Header -->
            <div class="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 flex-shrink-0"
                :class="{ 'mt-12': showSuccessMessage }">
                <div class="min-w-0 flex-1">
                    <h2 class="text-xl sm:text-2xl font-bold text-gray-900 truncate">Edit Shopping List</h2>
                    <p class="text-gray-600 mt-1 text-sm sm:text-base truncate">Manage "{{ shoppingList?.title }}"</p>
                </div>
                <button @click="attemptClose"
                    class="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 flex-shrink-0">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Tabs Navigation -->
            <div class="border-b border-gray-200">
                <nav class="flex" aria-label="Tabs">
                    <button @click="activeTab = 'info'"
                        :class="activeTab === 'info' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                        class="w-1/3 py-3 px-4 text-center border-b-2 font-medium text-sm sm:text-base transition-colors">
                        List Info
                    </button>
                    <button @click="activeTab = 'existing'"
                        :class="activeTab === 'existing' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                        class="w-1/3 py-3 px-4 text-center border-b-2 font-medium text-sm sm:text-base transition-colors">
                        Items ({{ currentItems.length }})
                    </button>
                    <button @click="activeTab = 'available'"
                        :class="activeTab === 'available' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                        class="w-1/3 py-3 px-4 text-center border-b-2 font-medium text-sm sm:text-base transition-colors">
                        Add Items
                    </button>
                </nav>
            </div>

            <!-- Tab Content -->
            <div class="flex-1 overflow-y-auto p-6">
                <!-- Tab 1: Shopping List Info & Status -->
                <div v-show="activeTab === 'info'" class="space-y-6 overflow-y-auto h-full">
                    <!-- Basic Info -->
                    <div class="bg-gray-50 rounded-lg border border-gray-200 p-6">
                        <h3 class="text-lg font-semibold text-gray-900 mb-6">Shopping List Info</h3>

                        <div class="space-y-6">
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-2">Title</label>
                                <input v-model="editForm.title" type="text"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-all">
                            </div>

                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                    <input :value="formatDate(editForm.start_date)" type="text"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                                        readonly>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                    <input :value="formatDate(editForm.end_date)" type="text"
                                        class="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                                        readonly>
                                </div>
                            </div>

                            <!-- Status Toggle -->
                            <div>
                                <label class="block text-sm font-medium text-gray-700 mb-4">Status</label>
                                <div class="grid grid-cols-2 gap-3">
                                    <label
                                        class="relative flex items-center justify-center p-4 border rounded-lg cursor-pointer transition-all"
                                        :class="editForm.status === 'active' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'">
                                        <input type="radio" v-model="editForm.status" value="active" class="sr-only">
                                        <div class="text-center">
                                            <div class="w-3 h-3 rounded-full mx-auto mb-2"
                                                :class="editForm.status === 'active' ? 'bg-primary-500' : 'bg-gray-300'">
                                            </div>
                                            <span class="text-sm font-medium">Active</span>
                                        </div>
                                    </label>
                                    <label
                                        class="relative flex items-center justify-center p-4 border rounded-lg cursor-pointer transition-all"
                                        :class="editForm.status === 'completed' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'">
                                        <input type="radio" v-model="editForm.status" value="completed" class="sr-only">
                                        <div class="text-center">
                                            <div class="w-3 h-3 rounded-full mx-auto mb-2"
                                                :class="editForm.status === 'completed' ? 'bg-primary-500' : 'bg-gray-300'">
                                            </div>
                                            <span class="text-sm font-medium">Completed</span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <!-- Total Price Display -->
                            <div class="bg-accent-50 p-4 rounded-lg border border-accent-200">
                                <div class="flex justify-between items-center">
                                    <span class="text-sm font-medium text-gray-700">Total Amount:</span>
                                    <span class="text-2xl font-bold text-accent-600">€{{ calculatedTotal.toFixed(2)
                                        }}</span>
                                </div>
                            </div>

                            <!-- Save Button -->
                            <button @click="saveShoppingList(true, true)" :disabled="savingList"
                                class="w-full inline-flex items-center justify-center px-6 py-4 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:ring-4 focus:ring-primary-100 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                <svg v-if="savingList" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none"
                                    viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                                <span v-if="savingList">Saving Changes...</span>
                                <span v-else>Save Changes</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Tab 2: Existing Items -->
                <div v-show="activeTab === 'existing'"
                    class="bg-gray-50 rounded-lg border border-gray-200 p-6 flex flex-col h-full overflow-hidden">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-semibold text-gray-900">Current Items</h3>
                        <div class="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded-full font-medium">
                            {{ currentItems.length }} items
                        </div>
                    </div>
                    <p class="text-sm text-gray-600 mb-4">Manage items currently in your shopping list</p>

                    <div v-if="loadingItems" class="flex justify-center py-12">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                    </div>

                    <div v-else-if="currentItems.length === 0" class="text-center py-12">
                        <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4">
                                </path>
                            </svg>
                        </div>
                        <p class="text-gray-500 font-medium mb-2">No items yet</p>
                        <p class="text-gray-400 text-sm">Add items using the form or from available items</p>
                    </div>

                    <div v-else class="flex-1 overflow-y-auto space-y-3">
                        <div v-for="item in currentItems" :key="item.id"
                            class="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary-200 hover:bg-primary-25 transition-all group">
                            <div class="flex-1 min-w-0">
                                <div class="font-medium text-gray-900 mb-1">{{ item.name }}</div>
                                <div class="text-sm text-gray-600 space-x-3">
                                    <span class="inline-flex items-center gap-1">
                                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 110 2h-1v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 010-2h4z">
                                            </path>
                                        </svg>
                                        {{ item.quantity }} {{ item.unit }}
                                    </span>
                                    <span class="inline-flex items-center gap-1">
                                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1">
                                            </path>
                                        </svg>
                                        €{{ item.price }}
                                    </span>
                                    <span
                                        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                        {{ item.type }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button @click="editItem(item)"
                                    class="inline-flex items-center px-3 py-1.5 bg-primary-500 text-white text-xs rounded-lg hover:bg-primary-600 transition-colors"
                                    title="Edit this item">
                                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                                        </path>
                                    </svg>
                                    Edit
                                </button>
                                <button @click="deleteItem(item)"
                                    class="inline-flex items-center px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors"
                                    title="Delete this item">
                                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                                        </path>
                                    </svg>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tab 3: Available Items to Add -->
                <div v-show="activeTab === 'available'" class="flex flex-col h-full overflow-hidden">
                    <!-- Create New Item Form -->
                    <CreateShoppingItemForm :shopping-list-id="shoppingList?.id" :all-ingredients="allIngredients"
                        @item-created="handleItemCreated" class="mb-6" />

                    <!-- Available Items Section -->
                    <div class="bg-gray-50 rounded-lg border border-gray-200 p-6 flex flex-col flex-1 overflow-hidden">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-lg font-semibold text-gray-900">Available Items</h3>
                            <div class="text-xs px-2 py-1 bg-secondary-100 text-secondary-700 rounded-full font-medium">
                                {{ existingShoppingItems.length }} available
                            </div>
                        </div>
                        <p class="text-sm text-gray-600 mb-4">Add items from your saved shopping items collection</p>

                        <div v-if="loadingShoppingItems" class="flex justify-center py-12">
                            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>

                        <div v-else-if="existingShoppingItems.length === 0" class="text-center py-12">
                            <div
                                class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                </svg>
                            </div>
                            <p class="text-gray-500 font-medium mb-2">No items available</p>
                            <p class="text-gray-400 text-sm">Create shopping items first to add them to lists</p>
                        </div>

                        <div v-else class="flex-1 overflow-y-auto space-y-3">
                            <div v-for="item in existingShoppingItems" :key="item.id"
                                class="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary-200 hover:bg-primary-25 transition-all group">
                                <div class="flex-1 min-w-0">
                                    <div class="font-medium text-gray-900 mb-1">{{ item.name }}</div>
                                    <div class="text-sm text-gray-600 space-x-3">
                                        <span class="inline-flex items-center gap-1">
                                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h4a1 1 0 110 2h-1v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6H3a1 1 0 010-2h4z">
                                                </path>
                                            </svg>
                                            {{ item.quantity }} {{ item.unit }}
                                        </span>
                                        <span class="inline-flex items-center gap-1">
                                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1">
                                                </path>
                                            </svg>
                                            €{{ item.price }}
                                        </span>
                                        <span
                                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                            {{ item.type }}
                                        </span>
                                    </div>
                                </div>
                                <div class="opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button @click="addExistingItem(item)"
                                        class="inline-flex items-center px-4 py-2 bg-secondary-500 text-white text-sm rounded-lg hover:bg-secondary-600 transition-colors"
                                        title="Add this item to your list">
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                        </svg>
                                        Add to List
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>

    <!-- Unsaved Changes Dialog -->
    <UnsavedChangesDialog :show="showUnsavedChangesDialog" @keep-editing="keepEditing" @discard="discardChanges" />

    <!-- Edit Shopping Item Modal -->
    <EditShoppingItemModal :show="showEditItemModal" :shopping-item="itemToEdit" @close="showEditItemModal = false"
        @updated="handleItemUpdated" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useApolloClient, useMutation } from '@vue/apollo-composable'
import { GET_SHOPPING_ITEMS, GET_ALL_INGREDIENTS, GET_SHOPPING_LIST } from '../../graphql/queries'
import { UPDATE_SHOPPING_LIST, DELETE_SHOPPING_ITEM } from '../../graphql/mutations'
import { showError } from '../../utils/errorHandling'
import CreateShoppingItemForm from './CreateShoppingItemForm.vue'
import EditShoppingItemModal from '../shopping-item/EditShoppingItemModal.vue'
import UnsavedChangesDialog from '../UnsavedChangesDialog.vue'
import dayjs from 'dayjs'

// Props
interface Props {
    show: boolean
    shoppingList: any
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
    close: []
    updated: []
}>()

// State
const loadingItems = ref(false)
const loadingShoppingItems = ref(false)
const loadingAllIngredients = ref(false)
const savingList = ref(false)
const showSuccessMessage = ref(false)
const successMessage = ref('')
const currentItems = ref([] as any[])
const existingShoppingItems = ref([] as any[])
const allIngredients = ref([] as any[])
const selectedItemForEdit = ref(null as any)
const activeTab = ref('info')

const editForm = ref({
    title: '',
    start_date: '',
    end_date: '',
    status: 'active'
})

const originalForm = ref({
    title: '',
    start_date: '',
    end_date: '',
    status: 'active'
})

const showUnsavedChangesDialog = ref(false)
const originalItemsCount = ref(0)
const showEditItemModal = ref(false)
const itemToEdit = ref(null as any)

// Apollo client
const { client } = useApolloClient()

// Mutations with proper cache updates
const { mutate: updateShoppingList } = useMutation(UPDATE_SHOPPING_LIST, {
    update: (cache) => {
        // Force refetch of the shopping list to ensure cache consistency
        cache.evict({ id: cache.identify({ __typename: 'ShoppingList', id: props.shoppingList?.id }) })
        cache.gc()
    },
    refetchQueries: () => {
        const queries = []
        if (props.shoppingList?.id) {
            queries.push({ query: GET_SHOPPING_LIST, variables: { id: props.shoppingList.id } })
        }
        return queries
    },
    awaitRefetchQueries: true
})

const { mutate: deleteShoppingItem } = useMutation(DELETE_SHOPPING_ITEM, {
    update: (cache, { data }) => {
        if (data?.deleteShoppingItem && props.shoppingList?.id) {
            // Remove the deleted item from cache
            cache.modify({
                id: cache.identify({ __typename: 'ShoppingList', id: props.shoppingList.id }),
                fields: {
                    items(existingItems = [], { readField }) {
                        return existingItems.filter(
                            (itemRef: any) => data.deleteShoppingItem !== readField('id', itemRef)
                        )
                    }
                }
            })
        }
    },
    refetchQueries: () => {
        const queries = []
        if (props.shoppingList?.id) {
            queries.push({ query: GET_SHOPPING_LIST, variables: { id: props.shoppingList.id } })
        }
        return queries
    },
    awaitRefetchQueries: true
})

// Computed
const calculatedTotal = computed(() => {
    const total = currentItems.value.reduce((total, item) => {
        // Just sum up the individual item prices (no quantity multiplication)
        const price = parseFloat(item.price) || 0
        return total + price
    }, 0)

    return total
})

const hasUnsavedChanges = computed(() => {
    return editForm.value.title !== originalForm.value.title ||
        editForm.value.status !== originalForm.value.status ||
        currentItems.value.length !== originalItemsCount.value
})

// Methods
const fetchCurrentItems = async () => {
    if (!props.shoppingList?.id) return

    loadingItems.value = true
    try {
        const result = await client.query({
            query: GET_SHOPPING_LIST,
            variables: { id: props.shoppingList.id },
            fetchPolicy: 'network-only'
        })
        // Only update if we have valid data
        if (result.data?.shoppingList?.items) {
            currentItems.value = [...result.data.shoppingList.items] // Create a new array to trigger reactivity
        }
    } catch (error) {
        console.error('Error fetching shopping list items:', error)
        showError(error, 'fetch shopping list items')
        // Don't clear items on error to preserve last known state
    } finally {
        loadingItems.value = false
    }
}

const fetchShoppingItemsData = async () => {
    loadingShoppingItems.value = true
    loadingAllIngredients.value = true

    try {
        // Fetch existing shopping items
        const itemsResult = await client.query({
            query: GET_SHOPPING_ITEMS,
            fetchPolicy: 'cache-first' // Use cache-first for better performance
        })
        if (itemsResult.data?.shoppingItems) {
            existingShoppingItems.value = itemsResult.data.shoppingItems
        }
    } catch (error) {
        showError(error, 'fetch shopping items')
    } finally {
        loadingShoppingItems.value = false
    }

    try {
        // Fetch all ingredients for the dropdown
        const ingredientsResult = await client.query({
            query: GET_ALL_INGREDIENTS,
            fetchPolicy: 'cache-first' // Use cache-first for better performance
        })
        if (ingredientsResult.data?.getAllIngredients) {
            allIngredients.value = ingredientsResult.data.getAllIngredients
        }
    } catch (error) {
        showError(error, 'fetch ingredients')
    } finally {
        loadingAllIngredients.value = false
    }
}

const saveShoppingList = async (showMessage = false, emitUpdate = false) => {
    if (!props.shoppingList?.id) return

    savingList.value = true
    try {
        await updateShoppingList({
            id: props.shoppingList.id,
            title: editForm.value.title,
            status: editForm.value.status,
            total_price: Math.round(calculatedTotal.value * 100) // Convert to cents
        })

        if (showMessage) {
            showTopBarSuccess('Shopping list updated successfully!')
        }

        // Only emit 'updated' when explicitly requested (manual save)
        if (emitUpdate) {
            emit('updated')
        }
    } catch (error) {
        showError(error, 'update shopping list')
    } finally {
        savingList.value = false
    }
}

const editItem = (item: any) => {
    itemToEdit.value = item
    showEditItemModal.value = true
}

const deleteItem = async (item: any) => {
    if (!confirm(`Are you sure you want to delete "${item.name}"?`)) {
        return
    }

    try {
        await deleteShoppingItem({ id: item.id })
        await fetchCurrentItems()
        // Auto-save the updated total
        await saveShoppingList()
        // Show success message
        showTopBarSuccess(`Deleted "${item.name}" from shopping list!`)
    } catch (error) {
        showError(error, 'delete shopping item')
    }
}

const handleItemCreated = async () => {
    // Refresh current items and available items
    await fetchCurrentItems()
    await fetchShoppingItemsData()
    selectedItemForEdit.value = null

    // Auto-save the updated total (without emitting update)
    await saveShoppingList(false, false)

    // Show success message
    showTopBarSuccess('Item added successfully!')
}

const handleItemUpdated = async () => {
    // Refresh current items after update
    await fetchCurrentItems()
    showEditItemModal.value = false
    itemToEdit.value = null

    // Auto-save the updated total
    await saveShoppingList(false, false)

    // Show success message
    showTopBarSuccess('Item updated successfully!')
}

const addExistingItem = (item: any) => {
    // When adding an existing item to the list, we want to add it as a new entry
    // This could be handled by copying the item's data to the create form
    // For now, we'll just edit it which allows the user to modify before adding
    itemToEdit.value = { ...item, id: null } // Clone item but remove ID to create new
    showEditItemModal.value = true
}

const formatDate = (date: string) => {
    if (!date) return ''
    return dayjs(date, 'DD/MM/YYYY').format('DD/MM/YYYY')
}

const showTopBarSuccess = (message: string) => {
    successMessage.value = message
    showSuccessMessage.value = true

    // Auto-hide after 3 seconds
    setTimeout(() => {
        showSuccessMessage.value = false
    }, 3000)
}

const attemptClose = () => {
    if (hasUnsavedChanges.value) {
        showUnsavedChangesDialog.value = true
    } else {
        closeModal()
    }
}

const closeModal = () => {
    // Don't clear data, just close modals and emit
    showSuccessMessage.value = false
    showUnsavedChangesDialog.value = false
    showEditItemModal.value = false
    selectedItemForEdit.value = null
    itemToEdit.value = null
    // Don't clear currentItems, existingShoppingItems, or allIngredients as they should persist
    emit('close')
}

const keepEditing = () => {
    showUnsavedChangesDialog.value = false
}

const discardChanges = () => {
    closeModal()
}

// Initialize form when shopping list changes
watch(() => props.shoppingList, (shoppingList) => {
    if (shoppingList) {
        const formData = {
            title: shoppingList.title || '',
            start_date: shoppingList.start_date || '',
            end_date: shoppingList.end_date || '',
            status: shoppingList.status || 'active'
        }
        editForm.value = { ...formData }
        originalForm.value = { ...formData }
    }
}, { immediate: true })

// Lifecycle
onMounted(() => {
    if (props.show && props.shoppingList) {
        fetchCurrentItems()
        fetchShoppingItemsData()
    }
})

// Watch for modal visibility changes
watch(() => props.show, async (show) => {
    if (show && props.shoppingList) {
        // Always fetch fresh data when opening
        await Promise.all([
            fetchCurrentItems(),
            fetchShoppingItemsData()
        ])
        originalItemsCount.value = currentItems.value.length
    }
    // Don't clear data when closing to prevent cache issues
})
</script>

<style scoped>
/* Custom scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}
</style>